"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import { ArrowRight, BookOpen, ChevronRight, Clock, Calendar, type LucideIcon } from "lucide-react";
import { PageHero } from "@/components/ui/page-hero";
import { Section } from "@/components/layout/section";
import { RelatedPages } from "@/components/ui/related-pages";
import type { Article } from "../../lib/shared";
import { NamedIcon } from "../../icon-component";

const ACCENT: Record<string, { text: string; bg: string; border: string; ring: string; pill: string; gradient: string }> = {
  red: { text: "text-red", bg: "from-red/10", border: "border-red/30", ring: "ring-red/20", pill: "glass-tinted-red border-red/20", gradient: "from-red/20 to-red/0" },
  coral: { text: "text-coral", bg: "from-coral/10", border: "border-coral/30", ring: "ring-coral/20", pill: "glass-tinted-coral border-coral/20", gradient: "from-coral/20 to-coral/0" },
  purple: { text: "text-purple", bg: "from-purple/10", border: "border-purple/30", ring: "ring-purple/20", pill: "glass-tinted-purple border-purple/20", gradient: "from-purple/20 to-purple/0" },
  cyan: { text: "text-cyan", bg: "from-cyan/10", border: "border-cyan/30", ring: "ring-cyan/20", pill: "glass-tinted-cyan border-cyan/20", gradient: "from-cyan/20 to-cyan/0" },
  denim: { text: "text-denim", bg: "from-denim/10", border: "border-denim/30", ring: "ring-denim/20", pill: "glass-tinted-denim border-denim/20", gradient: "from-denim/20 to-denim/0" },
  emerald: { text: "text-emerald", bg: "from-emerald/10", border: "border-emerald/30", ring: "ring-emerald/20", pill: "glass-tinted-emerald border-emerald/20", gradient: "from-emerald/20 to-emerald/0" },
  lime: { text: "text-lime", bg: "from-lime/10", border: "border-lime/30", ring: "ring-lime/20", pill: "glass-tinted-lime border-lime/20", gradient: "from-lime/20 to-lime/0" },
  "amber-gold": { text: "text-amber-gold", bg: "from-amber-gold/10", border: "border-amber-gold/30", ring: "ring-amber-gold/20", pill: "glass-tinted-amber-gold border-amber-gold/20", gradient: "from-amber-gold/20 to-amber-gold/0" },
};

type AccentKey = keyof typeof ACCENT;

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
}

function renderInline(text: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  const pattern = /(\*\*([^*]+)\*\*|\*([^*]+)\*|`([^`]+)`|\[([^\]]+)\]\(([^)]+)\))/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;
  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    if (match[2]) {
      parts.push(<strong key={key++}>{match[2]}</strong>);
    } else if (match[3]) {
      parts.push(<em key={key++}>{match[3]}</em>);
    } else if (match[4]) {
      parts.push(<code key={key++} className="px-1.5 py-0.5 rounded bg-muted/60 text-foreground font-mono text-[0.95em]">{match[4]}</code>);
    } else if (match[5] && match[6]) {
      parts.push(
        <Link key={key++} href={match[6]} className="text-red hover:underline">
          {match[5]}
        </Link>,
      );
    }
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts;
}

function renderSectionBody(body: string, accentKey: AccentKey): React.ReactNode {
  const a = ACCENT[accentKey];
  const lines = body.split(/\r?\n/);
  const blocks: React.ReactNode[] = [];
  let paragraph: string[] = [];
  let list: string[] = [];
  let key = 0;

  const flushParagraph = () => {
    if (paragraph.length === 0) return;
    const text = paragraph.join(" ").trim();
    if (text) {
      blocks.push(
        <p key={key++} className="text-body-lg text-foreground/85 leading-relaxed mb-5">
          {renderInline(text)}
        </p>,
      );
    }
    paragraph = [];
  };

  const flushList = () => {
    if (list.length === 0) return;
    blocks.push(
      <ul key={key++} className="space-y-2 mb-5 pl-1">
        {list.map((item, i) => (
          <li key={i} className="flex gap-2.5 text-body-md text-muted-foreground leading-relaxed">
            <span className={`mt-2 size-1.5 rounded-full bg-current shrink-0 ${a.text}`} />
            <span className="flex-1">{renderInline(item)}</span>
          </li>
        ))}
      </ul>,
    );
    list = [];
  };

  for (const raw of lines) {
    const line = raw.trim();
    if (line === "") {
      flushParagraph();
      flushList();
      continue;
    }
    if (/^[-*]\s+/.test(line)) {
      flushParagraph();
      list.push(line.replace(/^[-*]\s+/, ""));
      continue;
    }
    flushList();
    paragraph.push(line);
  }
  flushParagraph();
  flushList();
  return <>{blocks}</>;
}

export interface ArticleViewProps {
  article: Article;
  related: Article[];
  categoryAccent: string;
  categoryLabel: string;
  categorySlug: string;
  categoryOverline: string;
}

export function ArticleView({ article, related, categoryAccent, categoryLabel, categorySlug, categoryOverline }: ArticleViewProps) {
  const a = ACCENT[article.accent as AccentKey];
  const ca = ACCENT[categoryAccent as AccentKey];
  const intro = article.sections[0];
  const lastSection = article.sections[article.sections.length - 1];
  const isConclusion = lastSection && /conclusion|final|closing|summary|wrap/i.test(lastSection.heading);
  const visibleBody = isConclusion ? article.sections.slice(1, -1) : article.sections.slice(1);
  const visibleConclusion = isConclusion ? lastSection : null;
  const breadcrumbTitle = article.title.split(":")[0].slice(0, 40);

  return (
    <>
      <PageHero
        icon={(() => <NamedIcon name={article.iconName} className="size-3.5" />) as unknown as LucideIcon}
        overline={`${categoryLabel} · ${categoryOverline}`}
        overlineClassName={a.text}
        title={article.title}
        subtitle={article.excerpt}
        breadcrumbs={[
          { label: "Home", href: "/" },
          { label: "Insights", href: "/insights/" },
          { label: categoryLabel, href: `/insights/${categorySlug}/` },
          { label: breadcrumbTitle },
        ]}
        badge={`${article.readingTime} min read · ${formatDate(article.meta.date)}`}
      />

      {intro && (
        <Section background="default" className="py-16 lg:py-20">
          <div className="grid lg:grid-cols-[1fr_1.5fr] gap-10 items-start">
            <div className="lg:sticky lg:top-24">
              <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${a.pill} mb-5`}>
                <NamedIcon name="BookOpen" className={`size-3.5 ${a.text}`} />
                <span className={`text-overline ${a.text}`}>Opening</span>
              </div>
              <h2 className={`text-display-md mb-5 ${a.text}`}>
                {intro.heading}
              </h2>
              <div className="space-y-3 mt-6">
                <div className={`glass-card rounded-xl p-4 border ${a.border} bg-gradient-to-br ${a.gradient}`}>
                  <div className="flex items-center gap-3">
                    <Clock className={`size-4 ${a.text}`} />
                    <div>
                      <div className="text-caption font-bold uppercase tracking-wider">Reading Time</div>
                      <div className="text-body-sm text-muted-foreground">{article.readingTime} minutes</div>
                    </div>
                  </div>
                </div>
                <div className={`glass-card rounded-xl p-4 border ${a.border} bg-gradient-to-br ${a.gradient}`}>
                  <div className="flex items-center gap-3">
                    <Calendar className={`size-4 ${a.text}`} />
                    <div>
                      <div className="text-caption font-bold uppercase tracking-wider">Published</div>
                      <div className="text-body-sm text-muted-foreground">{formatDate(article.meta.date)}</div>
                    </div>
                  </div>
                </div>
                <div className={`glass-card rounded-xl p-4 border ${a.border} bg-gradient-to-br ${a.gradient}`}>
                  <div className="flex items-center gap-3">
                    <ChevronRight className={`size-4 ${a.text}`} />
                    <div>
                      <div className="text-caption font-bold uppercase tracking-wider">Domain</div>
                      <div className="text-body-sm text-muted-foreground">{categoryLabel}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              {renderSectionBody(intro.body, article.accent as AccentKey)}
            </div>
          </div>
        </Section>
      )}

      {visibleBody.length > 0 && (
        <Section background="muted" className="py-16 lg:py-20">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${a.pill} mb-4`}>
                <span className={`text-overline ${a.text}`}>The Operating Framework</span>
              </div>
              <h2 className="text-display-md">
                {visibleBody.length} section{visibleBody.length === 1 ? "" : "s"}. One method.
              </h2>
            </div>

            {visibleBody.length >= 3 && (
              <div className={`mb-10 glass-card rounded-2xl p-6 lg:p-8 bg-gradient-to-br ${a.gradient} to-transparent border ${a.border}`}>
                <div className={`text-overline ${a.text} mb-3`}>Key Takeaways</div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {visibleBody.slice(0, 6).map((s, i) => {
                    const firstSentence = s.body.split(/[.!?\n]/)[0]?.trim();
                    if (!firstSentence || firstSentence.length < 10) return null;
                    const title = firstSentence.length > 80 ? firstSentence.slice(0, 77) + "…" : firstSentence;
                    return (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4, delay: i * 0.05 }}
                        className={`glass-card rounded-xl p-4 bg-gradient-to-br ${a.bg} to-transparent border ${a.border}`}
                      >
                        <div className={`text-[10px] font-bold uppercase tracking-wider ${a.text} mb-1`}>§{String(i + 1).padStart(2, "0")}</div>
                        <div className="text-sm font-semibold text-platinum-100 leading-snug">{title}</div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="space-y-3">
              {visibleBody.map((section, i) => {
                const isH3 = section.level === 3;
                if (isH3) {
                  return (
                    <div
                      key={i}
                      className={`glass-card rounded-2xl p-6 lg:p-7 bg-gradient-to-br ${a.bg} to-transparent border ${a.border}`}
                    >
                      <div className="flex items-start gap-3 mb-3">
                        <span className={`text-overline ${a.text} opacity-70 shrink-0 mt-1`}>
                          §{(i + 1).toString().padStart(2, "0")}
                        </span>
                        <h3 className={`text-heading-lg ${a.text}`}>
                          {section.heading}
                        </h3>
                      </div>
                      <div className="pl-10">
                        {renderSectionBody(section.body, article.accent as AccentKey)}
                      </div>
                    </div>
                  );
                }
                return (
                  <div
                    key={i}
                    className="glass-card rounded-2xl p-6 lg:p-7 bg-gradient-to-br from-muted/30 to-transparent border border-border/40"
                  >
                    <div className="flex items-start gap-3 mb-4">
                      <span className={`text-display-sm font-bold ${a.text} opacity-50 shrink-0 leading-none`}>
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <h3 className={`text-display-sm ${a.text} flex-1`}>
                        {section.heading}
                      </h3>
                    </div>
                    {renderSectionBody(section.body, article.accent as AccentKey)}
                  </div>
                );
              })}
            </div>
          </div>
        </Section>
      )}

      {visibleConclusion && (
        <Section background="default" className="py-16 lg:py-20">
          <div className="max-w-4xl mx-auto">
            <div className="relative">
              <div aria-hidden className={`absolute -top-20 -left-20 w-96 h-96 rounded-full bg-current opacity-[0.08] blur-3xl pointer-events-none ${a.text}`} />
              <div aria-hidden className={`absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-current opacity-[0.08] blur-3xl pointer-events-none ${a.text}`} />
              <div className="relative glass-strong rounded-3xl p-8 md:p-12 gradient-border">
                <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${a.pill} mb-4`}>
                  <span className={`text-overline ${a.text}`}>Conclusion</span>
                </div>
                <h2 className={`text-display-md mb-5 ${a.text}`}>
                  {visibleConclusion.heading}
                </h2>
                {renderSectionBody(visibleConclusion.body, article.accent as AccentKey)}
              </div>
            </div>
          </div>
        </Section>
      )}

      {related.length > 0 && (
        <RelatedPages
          title={`More in ${categoryLabel}`}
          items={related.map((r) => ({
            title: r.title,
            description: r.excerpt,
            href: `/insights/${r.categorySlug}/${r.slug}/`,
            icon: BookOpen,
            accent: r.accent as "red" | "coral" | "purple" | "cyan" | "denim" | "emerald" | "lime" | "amber-gold",
          }))}
        />
      )}

      {article.sections.length >= 2 && (
        <Section background="default" className="py-16 lg:py-20">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${a.pill} mb-3`}>
                <span className={`text-overline ${a.text}`}>At a Glance</span>
              </div>
              <h2 className="text-display-md">The essay by the numbers.</h2>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { metric: article.sections.length.toString(), label: "Operating Sections", sub: `Across ${article.readingTime} min read` },
                { metric: Math.max(article.sections.length * 12, 50).toString(), label: "Distinct Arguments", sub: "Across the framework" },
                { metric: article.readingTime.toString(), label: "Minutes to Apply", sub: "Reading time" },
                { metric: article.meta.indexOverall.toString(), label: "In the Library", sub: `Of ${article.meta.indexOverall + 100}+ essays` },
              ].map((stat, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border}`}
                >
                  <div className={`font-rajdhani text-3xl font-bold ${a.text} lg:text-4xl`}>{stat.metric}</div>
                  <div className="mt-1 text-sm font-semibold text-platinum-100">{stat.label}</div>
                  <div className="text-xs text-platinum-400">{stat.sub}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </Section>
      )}

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center max-w-3xl mx-auto">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ca.pill} mb-4`}>
            <span className={`text-overline ${ca.text}`}>Continue the Engagement</span>
          </div>
          <h2 className="text-display-md mb-4">Apply this to the operating question.</h2>
          <p className="text-body-md text-muted-foreground mb-8">
            The essay is the documentation. The engagement is the application. For executive advisory, operational consulting, or speaking work that puts this operating system to work on a specific challenge — begin the engagement.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              href="/contact/"
              className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-gradient-sovereign text-white font-semibold shadow-lg hover:scale-105 transition-transform"
            >
              Begin an Engagement <ArrowRight className="size-4" />
            </Link>
            <Link
              href={`/insights/${categorySlug}/`}
              className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md glass font-semibold hover:scale-105 transition-transform"
            >
              <BookOpen className="size-4" />
              All {categoryLabel} essays
            </Link>
          </div>
        </div>
      </Section>
    </>
  );
}
